import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ isOpen, onClose }) => {
  // Use P3.jpg as the 1-gallon jug image - best product shot with clear label
  // Available images: P1.jpg through P9.jpg
  const jugImage = '/assets/P3.jpg';

  const benefits = [
    'Can be used anywhere',
    'Can be applied by anyone',
    'Applied with regular easy-to-use tools'
  ];

  const mixingInfo = [
    { label: 'Mix with', value: 'Type M mortar mix' },
    { label: 'Ratio', value: '1 gallon + 40-50 lbs Type M mortar mix' },
    { label: 'Coverage', value: 'Approx. 50 square feet' }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-2xl font-bold text-gray-900">Flexcrete 1-Gallon Jug</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                  {/* Left Side: Benefits with lines */}
                  <div className="lg:col-span-4 space-y-4 relative">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Key Benefits from Label
                    </h3>
                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        className="relative"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {/* Benefit Box */}
                        <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 border-l-4 border-primary-500 relative z-10">
                          <div className="flex items-start gap-3">
                            <CheckIcon className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm font-medium text-gray-800">{benefit}</p>
                          </div>
                        </div>
                        
                        {/* Animated line connecting to label (visible on desktop) */}
                        <div className="hidden lg:block absolute left-full top-1/2 w-12 h-0.5 bg-primary-500 transform -translate-y-1/2 z-0" 
                             style={{ marginLeft: '-1rem' }}>
                          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-primary-500 rounded-full"></div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Center: Jug Image */}
                  <div className="lg:col-span-4 flex justify-center relative">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="relative"
                    >
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 shadow-lg relative">
                        <img
                          src={jugImage}
                          alt="Flexcrete 1-Gallon Jug - All Purpose Do It Yourself Waterproofer"
                          className="w-full h-auto max-h-[400px] object-contain"
                          onError={(e) => {
                            // Fallback chain: try P2, then P1, then waterarmor.jpg
                            const target = e.target as HTMLImageElement;
                            if (target.src.includes('P3.jpg')) {
                              target.src = '/assets/P2.jpg';
                            } else if (target.src.includes('P2.jpg')) {
                              target.src = '/assets/P1.jpg';
                            } else {
                              target.src = '/assets/waterarmor.jpg';
                            }
                          }}
                        />
                        {/* Label highlight area - positioned where label would be on jug */}
                        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-yellow-300 bg-opacity-30 rounded-lg px-3 py-1 border-2 border-yellow-400 border-dashed pointer-events-none">
                          <p className="text-xs font-semibold text-yellow-900">Label</p>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Right Side: Mixing Information */}
                  <div className="lg:col-span-4 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Mixing Instructions
                    </h3>
                    {mixingInfo.map((info, index) => (
                      <motion.div
                        key={index}
                        className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-l-4 border-green-500"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + 0.3 }}
                      >
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                            {info.label}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {info.value}
                          </span>
                        </div>
                      </motion.div>
                    ))}

                    {/* Additional Info Box */}
                    <motion.div
                      className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <p className="text-xs text-blue-800">
                        <strong>Note:</strong> Mix thoroughly for 2-3 minutes until homogenous. 
                        Let false set for 90-120 seconds, then remix for 60-90 seconds before application.
                      </p>
                    </motion.div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => {
                        // Stripe integration will be added later
                        alert('Stripe payment integration coming soon!');
                      }}
                      className="flex-1 btn-primary text-lg py-3"
                    >
                      Buy Now
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 btn-secondary text-lg py-3"
                    >
                      Continue Shopping
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-4">
                    Payment processing will be available soon
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PurchaseModal;
